import { QRCodeSVG } from "qrcode.react";
import { ASSETS } from "@/lib/constants";

/**
 * Shared printable QR card block used by /qr-card and /qr-share.
 *
 * Props:
 *   qrUrl      — URL to encode in the QR
 *   variant    — "single" (4×6") or "bundle" (3.5×5", 4-up)
 *   mono       — true renders everything black-on-white for
 *                cheap toner / black & white printers
 *   headline   — react node; the big two-line title
 *   subhead    — react node; short sentence under the headline
 *   scanLabel  — mono footer text under the QR ("scan to …")
 *   embedLogo  — true to embed the app icon in the QR center. B&W
 *                cards skip the color logo automatically.
 */
export default function QrCardBlock({
  qrUrl,
  variant = "single",
  mono = false,
  headline,
  subhead,
  scanLabel = "scan to stay in the loop",
  embedLogo = true,
}) {
  const dims =
    variant === "bundle"
      ? { w: "3.5in", h: "5in", pad: "16px", title: 22, body: 11, logoImg: 26, foot: 14 }
      : { w: "4in", h: "6in", pad: "24px", title: 26, body: 12, logoImg: 32, foot: 16 };

  const accent = mono ? "#000000" : "#FF2A2A";
  const subtle = mono ? "rgba(0,0,0,0.55)" : "rgba(0,0,0,0.60)";

  return (
    <div
      className="qr-card-printable bg-white text-black flex flex-col items-center justify-between"
      style={{
        width: dims.w,
        height: dims.h,
        padding: dims.pad,
        border: "2px solid #000",
        boxSizing: "border-box",
      }}
      data-testid={`qr-card-printable-${variant}${mono ? "-mono" : ""}`}
    >
      {/* Header */}
      <div className="w-full">
        <div
          className="font-mono uppercase"
          style={{
            fontSize: "8px",
            letterSpacing: "0.28em",
            color: accent,
          }}
        >
          // for ER + ICU nurses
        </div>
        {headline ? (
          <h2
            className="mt-2 font-display font-extrabold leading-[0.95] tracking-tight text-black"
            style={{ fontSize: `${dims.title}px` }}
          >
            {headline(accent)}
          </h2>
        ) : null}
        {subhead ? (
          <p
            className="mt-2 leading-snug"
            style={{ fontSize: `${dims.body}px`, color: "rgba(0,0,0,0.75)" }}
          >
            {subhead}
          </p>
        ) : null}
      </div>

      {/* QR */}
      <div className="flex flex-col items-center gap-2">
        <div className="p-2 bg-white border border-black/10">
          <QRCodeSVG
            value={qrUrl}
            size={variant === "bundle" ? 128 : 168}
            level="M"
            includeMargin={false}
            fgColor="#000000"
            bgColor="#ffffff"
            imageSettings={
              embedLogo && !mono
                ? {
                    src: ASSETS.appIcon,
                    height: dims.logoImg,
                    width: dims.logoImg,
                    excavate: true,
                  }
                : undefined
            }
          />
        </div>
        <div
          className="font-mono uppercase tracking-[0.22em]"
          style={{ fontSize: "9px", color: subtle }}
        >
          {scanLabel}
        </div>
      </div>

      {/* Footer — brand only, no pricing */}
      <div className="w-full text-center">
        <div
          className="font-display font-extrabold tracking-tight text-black"
          style={{ fontSize: `${dims.foot}px` }}
        >
          stableish.me
        </div>
        <div
          className="mt-1 font-mono uppercase tracking-[0.22em]"
          style={{ fontSize: "8.5px", color: subtle }}
        >
          the ED &amp; ICU nurse reference app
        </div>
      </div>
    </div>
  );
}
